import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import { useMemo } from "react";
import { useCallback } from "react";
import {
  ComplianceProgramListItemFragment,
  ComplianceProgramState,
  DeleteProgramMutation,
  DeleteProgramMutationVariables,
  ExportComplianceProgramToCsvMutationMutation,
  ExportComplianceProgramToCsvMutationMutationVariables,
  GetComplianceProgramQuery,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import exportComplianceProgramToCsvMutation from "@apps/programs/graphql/export-compliance-program-to-csv-mutation";
import {
  currentOrganizationVar,
  currentUserVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown, { DropdownOptionType } from "@components/dropdown/dropdown";
import { useLink } from "@components/link/link";
import useConfirm from "@helpers/hooks/use-confirm";

import createOrUpdateComplianceProgramMutation from "../graphql/create-or-update-compliance-program-mutation";
import deleteComplianceProgramMutation from "../graphql/delete-compliance-program-mutation";
import getComplianceProgramQuery from "../graphql/get-compliance-program-admin-query";
import getComplianceProgramsQuery from "../graphql/get-compliance-programs-query";

const ComplianceProgramActionDropdown = ({
  complianceProgram,
}: {
  complianceProgram:
    | NonNullable<GetComplianceProgramQuery["complianceProgram"]>
    | ComplianceProgramListItemFragment;
}) => {
  const link = useLink();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();

  const complianceProgramId = complianceProgram.id;

  const { ConfirmationDialog: ConfirmDeleteDialog, confirm: confirmDelete } =
    useConfirm(
      "Are you sure?",
      "Are you sure you want to delete this program? All assessment data will be deleted."
    );

  const [saveComplianceProgram, { loading: isPublishingProgram }] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const [exportToCSV, { loading: isExportingProgram }] = useMutation<
    ExportComplianceProgramToCsvMutationMutation,
    ExportComplianceProgramToCsvMutationMutationVariables
  >(exportComplianceProgramToCsvMutation);

  const [deleteProgram, { loading: isDeletingProgram }] = useMutation<
    DeleteProgramMutation,
    DeleteProgramMutationVariables
  >(deleteComplianceProgramMutation);

  const errorMatches = useMemo(
    () => [
      {
        match: "no actions to take",
        title: "Add at least one step to the program before publishing",
      },
    ],
    []
  );

  const publishProgram = useCallback(() => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Published,
        organizationId: organization.id,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: "Program published",
        }),
    });
  }, [complianceProgramId, organization, errorMatches, saveComplianceProgram]);

  const exportProgram = useCallback(() => {
    if (complianceProgram.engagementSurveyTemplate) {
      return;
    }
    exportToCSV({
      variables: { complianceProgramId },
      context: { errorMatches },
    });
    successNotificationVar({
      title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
    });
  }, [
    exportToCSV,
    complianceProgramId,
    errorMatches,
    currentUser,
    complianceProgram,
  ]);

  const handleDeleteProgram = useCallback(async () => {
    const confirm = await confirmDelete();
    if (!confirm) return;
    deleteProgram({
      variables: {
        complianceProgramId,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramsQuery],
      onCompleted: () => {
        successNotificationVar({ title: "Program deleted" });
        link.redirect(`/programs`);
      },
    });
  }, [confirmDelete, deleteProgram, complianceProgramId, errorMatches, link]);

  const handlePauseProgram = useCallback(async () => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Paused,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: "Program paused",
        }),
    });
  }, [saveComplianceProgram, complianceProgramId, errorMatches]);

  const dropdownOptions: DropdownOptionType[] = useMemo(() => {
    return compact([
      {
        label: "Duplicate program",
        onClick: () =>
          link.redirect(`/programs/${complianceProgram.id}/duplicate`),
        disabled:
          isPublishingProgram || isDeletingProgram || isExportingProgram,
      },
      complianceProgram.canUpdate?.permission && {
        label: "Edit program",
        onClick: () => link.redirect(`/programs/${complianceProgram.id}/edit`),
        disabled:
          isPublishingProgram || isDeletingProgram || isExportingProgram,
      },
      complianceProgram.canUpdate?.permission && { type: "divider" },
      complianceProgram.canUpdate?.permission &&
        complianceProgram.state === ComplianceProgramState.Published && {
          label: "Pause program",
          onClick: handlePauseProgram,
          disabled:
            isPublishingProgram || isDeletingProgram || isExportingProgram,
        },
      complianceProgram.canUpdate?.permission &&
        complianceProgram.state !== ComplianceProgramState.Published && {
          label:
            complianceProgram.state === ComplianceProgramState.Draft
              ? "Publish"
              : "Resume",
          onClick: publishProgram,
          disabled: isPublishingProgram,
        },
      complianceProgram.state === ComplianceProgramState.Published && {
        type: "divider",
      },
      complianceProgram.state === ComplianceProgramState.Published &&
        !complianceProgram.engagementSurveyTemplate &&
        complianceProgram.canGenerateReport?.permission && {
          label: "Export to CSV",
          onClick: exportProgram,
          disabled: isExportingProgram,
        },
      complianceProgram.canUpdate?.permission && { type: "divider" },
      complianceProgram.canDelete?.permission && {
        label: "Delete program",
        onClick: handleDeleteProgram,
        textColor: "text-red-700 hover:text-red-900",
        disabled:
          isPublishingProgram || isDeletingProgram || isExportingProgram,
      },
    ]);
  }, [
    complianceProgram.canDelete?.permission,
    complianceProgram.canUpdate?.permission,
    complianceProgram.canGenerateReport?.permission,
    complianceProgram.id,
    complianceProgram.state,
    complianceProgram.engagementSurveyTemplate,
    exportProgram,
    handleDeleteProgram,
    handlePauseProgram,
    isDeletingProgram,
    isExportingProgram,
    isPublishingProgram,
    link,
    publishProgram,
  ]);

  if (dropdownOptions.length === 0) return null;

  return (
    <>
      <Dropdown
        options={dropdownOptions}
        aria-label="Compliance program actions"
      />
      <ConfirmDeleteDialog />
    </>
  );
};
export default ComplianceProgramActionDropdown;
